import styled from 'styled-components';
import { Slideout, Button } from '@makemydeal/dr-ui-bricks-components';
import { BreakPoints } from '@makemydeal/dr-ui-bricks-utils';
import { CUSTOMER_CARD_SLIDEOUT_PANEL_WIDTH_DESKTOP, CUSTOMER_CARD_SLIDEOUT_PANEL_WIDTH_MOBILE } from '../../constants';

// Slightly bolder than the tertiary button's default weight so the Cancel link reads a touch stronger.
export const StyledCancelButton = styled(Button)`
    font-weight: 600;
`;

// Wraps the save button so `aria-busy` has a home: the ui-bricks Button drops rest props, so the
// attribute is declared here instead. Kept inline-flex so it doesn't disturb the footer's layout.
export const StyledSaveButtonWrapper = styled.span`
    display: inline-flex;
`;

export const StyledSaveButtonContent = styled.span`
    display: inline-grid;
    place-items: center;

    & > * {
        grid-area: 1 / 1;
    }
`;

export const StyledSaveButtonLabel = styled.span<{ $hidden?: boolean }>`
    visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
`;

export const StyledSaveButtonSpinner = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    color: inherit;
`;

/** Scrollable card region. Grows to fill the slideout body so the footer below stays fixed.
      Flush by default (financing reuses it as-is); the slideout re-insets it — see StyledSlideout. */
export const StyledShowroomCardScroll = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 0 4px 16px;

    /* Slimmer scrollbar (Firefox + WebKit) */
    scrollbar-width: thin;
    &::-webkit-scrollbar {
        width: 6px;
    }
`;

/** Fixed header row above the scrollable card body: optional title (left) + slideout close ✕ (right).
    Horizontal padding matches the scroll region so they line up. */
export const StyledCardHeader = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 4px 8px;
`;

/** Right-aligned group in the header row: the slideout close ✕. */
export const StyledHeaderActions = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
`;

/** Icon + text layout for the Convert-to-Business CTA (ui-bricks Button has no leading-icon prop).
    Colors the label + icon (via currentColor) with the Interstate link blue MV uses. */
export const StyledCtaLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    white-space: nowrap;
    color: ${({ theme }) => theme.tokens?.ComponentLinkColor || '#0D65BF'};

    svg {
        width: 1rem;
        height: 1rem;
    }
`;

/** Card-owned close ✕ for the slideout (the cox chrome heading is hidden in details view). */
export const StyledSlideoutClose = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin: 0;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    color: ${({ theme }) => theme.tokens?.BaseColorGray600 || 'rgb(90, 90, 90)'};

    svg {
        width: 1.25rem;
        height: 1.25rem;
    }
`;

export const StyledCustomerCardFooter = styled.div`
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    /* Flush by default (financing); the slideout re-insets it — see StyledSlideout. */
    padding: 8px 0;
    background-color: ${({ theme }) => theme.tokens.BaseColorGray50};
    border-top: 1px solid ${({ theme }) => theme.tokens.BaseColorGray300};
`;

export const StyledSlideout = styled(Slideout)<{ $isLoading?: boolean; $detailsView?: boolean }>`
    ${({ $isLoading }) =>
        $isLoading &&
        `
        .dr-slideout-backdrop {
            pointer-events: none !important;
        }
    `}
    ${({ $detailsView }) =>
        $detailsView &&
        `
        /* Panel width mirrors dash's customer slideout: full-bleed below DESKTOP, fixed 956px at/above.
           Both rules need !important to beat the cox Slideout's own panelSize rule (min-width: 789px). */
        .dr-slideout-content-container {
            overflow: hidden;
            width: ${CUSTOMER_CARD_SLIDEOUT_PANEL_WIDTH_MOBILE} !important;
            @media (min-width: ${BreakPoints.DESKTOP}) {
                width: ${CUSTOMER_CARD_SLIDEOUT_PANEL_WIDTH_DESKTOP} !important;
            }
        }
        .dr-slideout-content {
            flex: 1 1 auto;
            min-height: 0;
            height: auto;
            display: flex;
            flex-direction: column;
            /* Scroll region and footer own the horizontal inset (below) so they span the full card. */
            padding-inline: 0;
            padding-bottom: 0;
        }
        /* Re-inset the shared card for the slideout: padding on the scroll region keeps the scrollbar
           at the card edge with content inset, and the footer keeps its full-width background. */
        /* The card renders its own header (title + Convert-to-Business + close), so hide the cox chrome title bar. */
        .dr-slideout-heading {
            display: none;
        }
        ${StyledCardHeader} {
            padding: 16px 24px 8px;
        }
        ${StyledShowroomCardScroll} {
            padding: 0 24px 16px;
        }
        ${StyledCustomerCardFooter} {
            padding: 8px 24px;
        }
    `}
`;
